import * as d3 from 'd3';
import { useEffect, useRef, useState } from 'react';

import { useIntl } from '../../hooks';
import type { TelemetryDataPoint } from '../../types/telemetry.js';

interface TelemetryLineChartProps {
  title: string;
  data: TelemetryDataPoint[];
  unit: string | null;
  isLoading?: boolean;
}

const MARGIN = { top: 16, right: 16, bottom: 40, left: 56 };

export function TelemetryLineChart({ title, data, unit, isLoading }: TelemetryLineChartProps) {
  const intl = useIntl();
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [dimensions, setDimensions] = useState({ width: 400, height: 200 });

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        setDimensions({ width, height: 200 });
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    if (isLoading || data.length === 0) return;

    const { width, height } = dimensions;
    const innerWidth = width - MARGIN.left - MARGIN.right;
    const innerHeight = height - MARGIN.top - MARGIN.bottom;

    if (innerWidth <= 0 || innerHeight <= 0) return;

    const points = data.map((d) => ({ ...d, date: new Date(d.timestamp) }));

    const xScale = d3
      .scaleTime()
      .domain(d3.extent(points, (d) => d.date) as [Date, Date])
      .range([0, innerWidth]);

    const yExtent = d3.extent(points, (d) => d.value) as [number, number];
    const yPadding = (yExtent[1] - yExtent[0]) * 0.1 || 1;
    const yScale = d3
      .scaleLinear()
      .domain([yExtent[0] - yPadding, yExtent[1] + yPadding])
      .nice()
      .range([innerHeight, 0]);

    svg.attr('width', width).attr('height', height);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

    // Grid lines
    g.append('g')
      .attr('class', 'grid')
      .call(
        d3
          .axisLeft(yScale)
          .tickSize(-innerWidth)
          .tickFormat(() => ''),
      )
      .call((axis) => axis.select('.domain').remove())
      .call((axis) =>
        axis
          .selectAll('.tick line')
          .attr('stroke', 'var(--color-border)')
          .attr('stroke-dasharray', '3,3'),
      );

    // X axis
    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(
        d3
          .axisBottom(xScale)
          .ticks(5)
          .tickFormat((d) => {
            const date = d as Date;
            return `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
          }),
      )
      .call((axis) => axis.select('.domain').attr('stroke', 'var(--color-border)'))
      .call((axis) => axis.selectAll('.tick line').attr('stroke', 'var(--color-border)'))
      .call((axis) =>
        axis.selectAll('text').attr('fill', 'var(--color-foreground)').style('font-size', '10px'),
      );

    // Y axis
    const yAxis = g
      .append('g')
      .call(d3.axisLeft(yScale).ticks(5))
      .call((axis) => axis.select('.domain').attr('stroke', 'var(--color-border)'))
      .call((axis) => axis.selectAll('.tick line').attr('stroke', 'var(--color-border)'))
      .call((axis) =>
        axis.selectAll('text').attr('fill', 'var(--color-foreground)').style('font-size', '10px'),
      );

    // Y axis label (unit)
    if (unit) {
      yAxis
        .append('text')
        .attr('transform', 'rotate(-90)')
        .attr('x', -innerHeight / 2)
        .attr('y', -44)
        .attr('text-anchor', 'middle')
        .attr('fill', 'var(--color-foreground)')
        .style('font-size', '11px')
        .text(unit);
    }

    // Line
    const line = d3
      .line<{ date: Date; value: number }>()
      .x((d) => xScale(d.date))
      .y((d) => yScale(d.value))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(points)
      .attr('fill', 'none')
      .attr('stroke', 'var(--color-accent)')
      .attr('stroke-width', 2)
      .attr('d', line);

    // Dots
    g.selectAll('circle')
      .data(points)
      .join('circle')
      .attr('cx', (d) => xScale(d.date))
      .attr('cy', (d) => yScale(d.value))
      .attr('r', points.length > 50 ? 0 : 3)
      .attr('fill', 'var(--color-accent)');

    // Tooltip overlay
    const tooltip = d3.select(containerRef.current).select<HTMLDivElement>('.chart-tooltip');

    const bisect = d3.bisector<{ date: Date; value: number }, Date>((d) => d.date).left;

    const verticalLine = g
      .append('line')
      .attr('class', 'vertical-line')
      .attr('stroke', 'var(--color-foreground)')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,2')
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .style('opacity', 0);

    const hoverDot = g
      .append('circle')
      .attr('r', 5)
      .attr('fill', 'var(--color-accent)')
      .attr('stroke', 'var(--color-background)')
      .attr('stroke-width', 2)
      .style('opacity', 0);

    g.append('rect')
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .attr('fill', 'transparent')
      .on('mousemove', (event) => {
        const [mx] = d3.pointer(event);
        const hoveredDate = xScale.invert(mx);
        const idx = bisect(points, hoveredDate, 1);
        const d0 = points[idx - 1];
        const d1 = points[idx];
        if (!d0) return;
        const d =
          d1 &&
          Math.abs(hoveredDate.getTime() - d0.date.getTime()) >
            Math.abs(hoveredDate.getTime() - d1.date.getTime())
            ? d1
            : d0;

        const cx = xScale(d.date);
        const cy = yScale(d.value);

        verticalLine.attr('x1', cx).attr('x2', cx).style('opacity', 1);
        hoverDot.attr('cx', cx).attr('cy', cy).style('opacity', 1);

        const containerRect = containerRef.current!.getBoundingClientRect();
        const svgRect = svgRef.current!.getBoundingClientRect();
        const tipX = svgRect.left - containerRect.left + MARGIN.left + cx;
        const tipY = svgRect.top - containerRect.top + MARGIN.top + cy;

        tooltip
          .style('opacity', 1)
          .style('left', `${tipX + 10}px`)
          .style('top', `${tipY - 28}px`)
          .text(
            `${d.date.getMonth() + 1}/${d.date.getDate()} ${d.date.getHours()}:${String(d.date.getMinutes()).padStart(2, '0')} · ${d.value}${unit ? ` ${unit}` : ''}`,
          );
      })
      .on('mouseleave', () => {
        verticalLine.style('opacity', 0);
        hoverDot.style('opacity', 0);
        tooltip.style('opacity', 0);
      });
  }, [data, dimensions, unit, isLoading, intl]);

  return (
    <div className="bg-background-light border border-border rounded-lg p-3 flex flex-col gap-2">
      <div className="text-sm font-medium text-foreground">{title}</div>
      <div ref={containerRef} className="relative w-full">
        <div
          className="chart-tooltip absolute pointer-events-none bg-background-light border border-border rounded px-2 py-1 text-xs text-foreground z-10 whitespace-nowrap"
          style={{ opacity: 0 }}
        />
        {isLoading ? (
          <div className="flex items-center justify-center h-[200px] text-foreground text-sm">
            {intl.formatMessage({ id: 'deviceTelemetry.chart.loading' })}
          </div>
        ) : data.length === 0 ? (
          <div className="flex items-center justify-center h-[200px] text-foreground text-sm">
            {intl.formatMessage({ id: 'deviceTelemetry.chart.noData' })}
          </div>
        ) : (
          <svg ref={svgRef} style={{ display: 'block', width: '100%', height: 200 }} />
        )}
      </div>
    </div>
  );
}
